import { useEffect, useState, useCallback, useRef } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../config/api';
import {
  getProjectDownloadLogs,
  getGlobalDownloadLogs,
  exportProjectDownloadLogs,
  exportGlobalDownloadLogs,
  type DocumentDownloadLog,
  type DocumentDownloadLogFilters,
  type DocumentDownloadLogListResponse,
  type DownloadLogExportFormat,
  type DownloadLogExportResult,
} from '../../api/downloadLogs';
import { DownloadLogFiltersBar } from './DownloadLogFiltersBar';
import { DownloadLogTable } from './DownloadLogTable';
import { DownloadLogPagination } from './DownloadLogPagination';
import { DownloadLogExportButton } from './DownloadLogExportButton';
import { areFiltersEqual } from './areFiltersEqual';
import { getStoredPageSize, storePageSize } from './pageSizeStorage';
import { getStoredFilters, storeFilters } from './filtersStorage';

interface DownloadLogsViewProps {
  scope: 'project' | 'global';
  projectId?: string;
}

/**
 * Shared container for both the project-scoped and global (system-admin)
 * download log screens. `scope` selects which API functions back it and
 * whether the table renders a Project column.
 */
export const DownloadLogsView = ({ scope, projectId }: DownloadLogsViewProps) => {
  const { handleAuthError } = useAuth();
  const [filters, setFilters] = useState<DocumentDownloadLogFilters>(() => getStoredFilters(scope, projectId));
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(getStoredPageSize);
  const [logs, setLogs] = useState<DocumentDownloadLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);

    const fetchOnce = () =>
      scope === 'project'
        ? getProjectDownloadLogs(projectId || '', filters, page, size)
        : getGlobalDownloadLogs(filters, page, size);

    const applyResult = (data: DocumentDownloadLogListResponse) => {
      setLogs(data.results);
      setTotal(data.total);
    };

    try {
      applyResult(await fetchOnce());
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setLoading(false);
        return;
      }

      const shouldRetry = await handleAuthError(err);
      if (shouldRetry) {
        try {
          applyResult(await fetchOnce());
          setLoading(false);
          return;
        } catch (retryErr) {
          console.error('Failed to load download logs after retry:', retryErr);
        }
      }

      console.error('Failed to load download logs:', err);
      setError('Failed to load the download log. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, projectId, filters, page, size]);

  // Guard against React StrictMode's dev-only double-invoke firing this load
  // twice on mount, by comparing the *identity* of the memoized fetchLogs
  // function rather than a plain boolean — StrictMode's double-invoke reuses
  // the same closure for both calls (no re-render in between), while a
  // genuine change (projectId, filters, page, size) produces a new closure
  // and must still trigger a real reload.
  const lastRanForRef = useRef<typeof fetchLogs | null>(null);
  useEffect(() => {
    if (lastRanForRef.current === fetchLogs) return;
    lastRanForRef.current = fetchLogs;
    fetchLogs();
  }, [fetchLogs]);

  const handleFiltersChange = (next: DocumentDownloadLogFilters) => {
    if (areFiltersEqual(filters, next)) return;
    setFilters(next);
    setPage(1);
    storeFilters(scope, projectId, next);
  };

  const handleSizeChange = (next: number) => {
    if (next === size) return;
    setSize(next);
    storePageSize(next);
    setPage(1);
  };

  const handleExport = async (
    format: DownloadLogExportFormat,
    exportFilters: DocumentDownloadLogFilters,
  ): Promise<DownloadLogExportResult> =>
    scope === 'project'
      ? exportProjectDownloadLogs(projectId || '', format, exportFilters)
      : exportGlobalDownloadLogs(format, exportFilters);

  if (forbidden) {
    return (
      <div className="py-12 text-center text-[#888]">
        You do not have permission to view the download log.
      </div>
    );
  }

  if (error) {
    return <div className="py-12 text-center text-red-600">{error}</div>;
  }

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <DownloadLogFiltersBar scope={scope} projectId={projectId} initialFilters={filters} onChange={handleFiltersChange} />
        <DownloadLogExportButton filters={filters} onExport={handleExport} />
      </div>
      <DownloadLogPagination page={page} size={size} total={total} onPageChange={setPage} onSizeChange={handleSizeChange} />
      <DownloadLogTable scope={scope} logs={logs} loading={loading} />
      <DownloadLogPagination page={page} size={size} total={total} onPageChange={setPage} onSizeChange={handleSizeChange} />
    </div>
  );
};
